import { UIElement } from '../ui-element';
import {
    dispatchNativeEvent,
    element,
    setInnerText,
    updateElement,
    setNativeInputValue,
} from '../../global/ui-helpers';
import { UIIcon } from '../icon/ui-icon';
import { UIIconGlyphRedesigns } from '../icon/ui-icon.helpers';
import { Digest } from '../../global/digest';
import { Labels } from '../../global/labels';
import { Component } from '../../decorators/component.decorator';
import { Attribute } from '../../decorators/attribute.decorator';
import { Child, Children } from '../../decorators/child.decorator';
import type { AttributeValue, ElementConfig } from '../ui-element.type';
import type { TranslationLabels } from '../../types/labels.type';
import type { EventHandler } from '../../global/utils.type';
import type { CustomFormComponent } from '../../types/custom-form-component';
import styles from './ui-rating.css';

/**
 * @class
 * @memberof SharedComponents
 * @augments {UIElement}
 * @classdesc Represents a class for <code>ui-rating</code> element.
 * @alias UIRating
 * @element ui-rating
 * @implements {CustomFormComponent}
 * @property {string} [labelMin] {@attr label-min} Label for minimum value.
 * @property {string} [labelMax] {@attr label-max} Label for maximum value.
 * @property {string} [labelLegend] {@attr label-legend} Label for accessibility
 * (hidden from user view).
 * @property {Function} [labelFn] {@attr label-fn} Optional function that takes in the rating integer
 * (starting from 1) and returns a label for that rating (eg "4 stars").
 * @property {string} [radiogroupName="ui-rating"] {@attr radiogroup-name} Name for radio buttons.
 * If not set, can be taken from control id.
 * @property {HTMLFieldSetElement} fieldset Fieldset of inputs with type radio.
 * @property {HTMLInputElement} control Main input for storing value.
 * @property {HTMLLegendElement} legend Shortcut for the fieldset legend used for accessibility.
 * Custom ids:
 * - set your custom id on the slotted input. If set, it is used for the hidden control,
 * fieldset (with prefix '-fieldset'), radio group inputs/labels (custom id + '-' +index), and ui-field label[for] when inside ui-field.
 * - if not set, a random id is generated for the previously mentioned elements.
 * @example
 * <ui-rating label-min="Difficult" label-max="Easy" label-legend="Rate this website">
 *   <input id="test-rating-field" type="range" min="1" max="5" step="1">
 * </ui-rating>
 */
@Component({
    tag: 'ui-rating',
    styles,
})
export class UIRating extends UIElement implements CustomFormComponent {
    @Attribute(String) labelMin: string = '';
    @Attribute(String) labelMax: string = '';
    @Attribute(String) labelLegend: string = '';

    @Child('fieldset') fieldset!: HTMLFieldSetElement;
    @Child('.ui-rating__control') control!: HTMLInputElement;
    @Child('legend') legend!: HTMLLegendElement;
    @Children('fieldset input[type="radio"]')
    radioInputs!: NodeListOf<HTMLInputElement>;

    labelFn?: (rating: number) => string;

    private observer!: MutationObserver;
    private fieldsetClickHandler!: EventHandler;
    private inputFocusHandler!: EventHandler<FocusEvent>;

    /**
     * Define labels that could be localised.
     * @type {Labels}
     * @readonly
     */
    static get labels(): TranslationLabels.Labels {
        return Labels.attach('ui-rating', {
            'rating-singular': 'star',
            'rating-plural': 'stars',
        });
    }

    /**
     * @type {Array<string>}
     */
    static get observedAttributes(): Array<string> {
        return ['label-min', 'label-max', 'label-legend'];
    }

    get radiogroupName(): string {
        return (
            this.getAttribute('radiogroup-name') ||
            this.control?.getAttribute('id') ||
            'ui-rating'
        );
    }

    set radiogroupName(value: string) {
        this.setAttribute('radiogroup-name', value);
    }

    get min(): number {
        return Number(this.control.min) || 1;
    }

    set min(value: number) {
        this.control.min = String(value);
    }

    get max(): number {
        return Number(this.control.max) || 5;
    }

    set max(value: number) {
        this.control.max = String(value);
    }

    get step(): number {
        return Number(this.control.step) || 1;
    }

    set step(value: number) {
        this.control.step = String(value);
    }

    get value(): number | null {
        return parseInt(this.control.value) || null;
    }

    set value(value: number | null) {
        setNativeInputValue(this.control, String(value ?? ''));
    }

    /**
     * Return value aligned to the range and steps.
     * @param {number} value
     * @returns {number | null}
     */
    alignValueToRange(value: number): number | null {
        if (value < this.min) {
            return null;
        }
        if (value > this.max) {
            return this.max;
        }
        return value;
    }

    /**
     * Sets a value to the controls.
     * @param {number | null} value
     * @fires change
     */
    setValue(value: number | null): void {
        this.value = this.alignValueToRange(Number(value));
        this.updateRadioFieldsetValue();
        dispatchNativeEvent(this.control, 'change', true);
        dispatchNativeEvent(this.control, 'input', true);
    }

    /**
     * Updating icons appearance according to the value.
     */
    updateRadioFieldsetValue(): void {
        this.querySelectorAll<HTMLInputElement>(
            'input[name=' + this.radiogroupName + ']'
        ).forEach((node) => {
            const icon = this.querySelector<UIIcon>(
                'label[for=' + node.getAttribute('id') + '] ui-icon'
            );
            const isFilled = Number(node.value) <= Number(this.value);
            icon!.glyph = isFilled
                ? UIIconGlyphRedesigns.RedesignStar
                : UIIconGlyphRedesigns.RedesignStarOutline;
            if (isFilled) {
                icon!.setAttribute('color', 'orange');
            } else {
                icon!.removeAttribute('color');
            }
        });
    }

    /**
     * Get the accessible label for a specific rating value.
     * @param {number} rating - Rating value starting at 1.
     * @returns {string}
     * @private
     */
    private getRatingLabel(rating: number): string {
        if (this.labelFn && typeof this.labelFn === 'function') {
            return this.labelFn(rating);
        }
        const label =
            rating === 1
                ? UIRating.labels['rating-singular']
                : UIRating.labels['rating-plural'];
        return `${rating} ${label}`;
    }

    /**
     * Build configs for radio fields.
     * Iterates from max to min so that DOM order is ascending (1…N), which is
     * required for the CSS sibling-selector hover effect to work correctly.
     * @returns {Array<ElementConfig>}
     */
    buildRadioFieldset(): Array<ElementConfig> {
        let radioFields: Array<ElementConfig> = [];
        for (let i = this.max; i >= this.min; i -= this.step) {
            radioFields = (
                [
                    {
                        tagName: 'input',
                        attributes: {
                            type: 'radio',
                            value: i,
                            name: this.radiogroupName,
                            id: this.radiogroupName + '-' + i,
                        },
                    },
                    {
                        tagName: 'label',
                        attributes: {
                            for: this.radiogroupName + '-' + i,
                        },
                        children: [
                            element`<span class="-visually-hidden">${this.getRatingLabel(i)}</span>`,
                            {
                                tagName: 'ui-icon',
                                attributes: {
                                    'glyph':
                                        UIIconGlyphRedesigns.RedesignStarOutline,
                                    'aria-hidden': 'true',
                                },
                            },
                        ],
                    },
                ] as Array<ElementConfig>
            ).concat(radioFields);
        }

        return radioFields;
    }

    /**
     * Fires callback when user clicked on a radio input.
     * @param {Event} e
     * @private
     */
    private handleRateClick(e: Event): void {
        e.preventDefault();
        this.setValue(Number((e.target as HTMLInputElement).value));
    }

    /**
     * Fires callback when a radio input receives focus, moving focus to the
     * last radio if the focus originates from outside the group and no value
     * is selected.
     * @param {FocusEvent} event
     * @private
     */
    private handleInputFocus(event: FocusEvent): void {
        const isInsideFocus = Array.from(this.radioInputs).includes(
            event.relatedTarget as HTMLInputElement
        );
        const shouldFocusLast = !isInsideFocus && !this.control.value;
        if (shouldFocusLast) {
            const maxRatingRadio =
                this.radioInputs[this.radioInputs.length - 1];
            maxRatingRadio.focus();
        }
    }

    /**
     * Fires callback when attributes of control mutates.
     * @param {Array<MutationRecord>} mutations
     * @private
     */
    private handleControlMutations(mutations: Array<MutationRecord>): void {
        for (const mutation of mutations) {
            if (mutation.type !== 'attributes') {
                continue;
            }
            if (['min', 'max', 'step'].includes(mutation.attributeName!)) {
                if (
                    mutation.oldValue !==
                    (mutation.target as Element).getAttribute(
                        mutation.attributeName!
                    )
                ) {
                    this.rebuildChildren.call(
                        this.fieldset.querySelector('.ui-rating__radio-group'),
                        this.buildRadioFieldset()
                    );
                    this.setValue(this.value);
                    return;
                }
            }
        }
    }

    /**
     * @inheritDoc
     */
    observeAttributes(
        name: string,
        _oldValue: AttributeValue,
        _newValue: string | null
    ): void {
        if (!this.hydrated) {
            return;
        }

        switch (name) {
            case 'label-min':
                setInnerText(
                    this.querySelector<HTMLElement>('.ui-rating__label.-min')!,
                    this.labelMin
                );
                break;
            case 'label-max':
                setInnerText(
                    this.querySelector<HTMLElement>('.ui-rating__label.-max')!,
                    this.labelMax
                );
                break;
            case 'label-legend':
                setInnerText(this.legend, this.labelLegend);
                break;
            /* istanbul ignore next */
            default:
                break;
        }
    }

    /**
     * Returns the control ID that should be used by ui-field for label association.
     * For ui-rating, this returns the hidden input's id.
     * Always returns a string, we generate id on hidden input if missing.
     * @returns {string}
     */
    getControlIdForField(): string {
        const hiddenInput =
            this.control ||
            this.querySelector<HTMLInputElement>(
                'input[type="hidden"].ui-rating__control'
            );
        if (hiddenInput) {
            let id = hiddenInput.getAttribute('id') || hiddenInput.id;
            if (!id) {
                id = Digest.randomId();
                hiddenInput.id = id;
                hiddenInput.setAttribute('id', id);
            }
            return id;
        }
        // Hidden input not yet rendered (e.g. ui-field called getControlIdForField during its render).
        // Read slotted input id so label[for] is correct before our render runs.
        const slottedInput = this.querySelector('input');
        const slottedId = slottedInput?.getAttribute('id')?.trim();
        if (slottedId) {
            return slottedId;
        }
        return Digest.randomId();
    }

    /**
     * @inheritDoc
     */
    getControlElement(): HTMLInputElement {
        return this.control;
    }

    /**
     * @inheritDoc
     */
    onValuePatched(newValue: number | null): void {
        if (newValue !== this.value) {
            this.setValue(newValue);
        }
    }

    /**
     * @inheritDoc
     */
    onDisabledStateChanged(newState: boolean): void {
        this.radioInputs.forEach((radio) => {
            radio.disabled = newState;
        });
    }

    /**
     * @private
     */
    private subscribeEvents(): void {
        this.fieldset?.addEventListener('click', this.fieldsetClickHandler);
        this.observer.observe(this.control, { attributes: true });
        for (const radio of this.radioInputs) {
            radio.addEventListener('focus', this.inputFocusHandler);
        }
    }

    /**
     * @private
     */
    private unsubscribeEvents(): void {
        this.fieldset?.removeEventListener('click', this.fieldsetClickHandler);
        this.observer.disconnect();
        for (const radio of this.radioInputs) {
            radio.removeEventListener('focus', this.inputFocusHandler);
        }
    }

    /**
     * @inheritDoc
     */
    render(): void {
        const nodes = this.detachChildNodes();
        const control = nodes.find((node) => node.localName === 'input') as
            | HTMLInputElement
            | undefined;

        const isInsideField = !!this.closest('ui-field');
        let inputId = control?.getAttribute('id');
        if (!inputId && !isInsideField) {
            inputId = Digest.randomId();
            if (control) {
                control.setAttribute('id', inputId);
            }
        }
        const baseId = inputId ?? Digest.randomId();
        const fieldsetId = `${baseId}-fieldset`;

        this.insertElements([
            {
                tagName: 'input',
                element: control ?? null,
                attributes: {
                    type: 'hidden',
                    id: baseId,
                },
                classList: {
                    'ui-rating__control': true,
                },
            },
            {
                tagName: 'span',
                classList: {
                    'ui-rating__label': true,
                    '-min': true,
                },
                children: [this.labelMin],
            },
            {
                tagName: 'fieldset',
                attributes: {
                    id: fieldsetId,
                },
                children: [
                    {
                        tagName: 'legend',
                        classList: {
                            '-visually-hidden': true,
                        },
                        children: [this.labelLegend],
                    },
                    {
                        tagName: 'div',
                        classList: {
                            'ui-rating__radio-group': true,
                        },
                    },
                ],
            },
            {
                tagName: 'span',
                classList: {
                    'ui-rating__label': true,
                    '-max': true,
                },
                children: [this.labelMax],
            },
        ]);

        updateElement(
            this.fieldset.querySelector<HTMLDivElement>(
                '.ui-rating__radio-group'
            )!,
            {
                children: this.buildRadioFieldset(),
            }
        );
        this.value = parseInt(this.control.getAttribute('value') ?? '');
        this.setValue(this.value);
    }

    /**
     * @inheritDoc
     */
    hydrate(): void {
        this.fieldsetClickHandler = (e: Event) => {
            const target = e.target as HTMLInputElement;
            if (target.localName === 'input' && target.type === 'radio') {
                this.handleRateClick(e);
            }
        };
        this.inputFocusHandler = this.handleInputFocus.bind(
            this
        ) as EventListener;
        this.observer = new MutationObserver(
            this.handleControlMutations.bind(this)
        );
        this.subscribeEvents();
    }

    /**
     * @inheritDoc
     */
    disconnect(): void {
        this.unsubscribeEvents();
    }

    /**
     * @inheritDoc
     */
    reconnect() {
        this.subscribeEvents();
    }
}
